// ContractHealthSummary — 4-card cross-tab strip on the contract
// Details surface. Each card surfaces a count from a different tab and
// navigates there with the appropriate filter pre-applied. Insurance
// expiring uses amber, matching the insurance traffic-light vocabulary
// (current=green → expiring=amber → expired=red) on the Insurances tab;
// deliverables use red for overdue; variations use amber for proposed;
// zero counts use slate.

import { CrossTabSummaryCard } from "./CrossTabSummaryCard";
import { buildInsuranceUrl } from "../tracking/insurance/insuranceUrlState";
import type { ContractHealth } from "../../../lib/contract-overview/contractHealth";

interface HasRecordsFlags {
  deliverables: boolean;
  milestones: boolean;
  insurance: boolean;
  variations: boolean;
}

interface ContractHealthSummaryProps {
  contractId: string;
  health: ContractHealth;
  /** When the contract has zero records of a category at all, the card
   *  switches to a "no X tracked yet" empty state rather than a
   *  reassuring "nothing overdue" message. Defaults to true (records
   *  exist) for each category. */
  hasRecords?: Partial<HasRecordsFlags>;
}

function deliverablesUrl(contractId: string): string {
  const params = new URLSearchParams();
  params.set("tab", "deliverable");
  params.set("filters[status]", "overdue");
  return `/manage/contracts/${contractId}?${params.toString()}`;
}

function milestonesUrl(contractId: string): string {
  const params = new URLSearchParams();
  params.set("tab", "milestone");
  params.set("filters[status]", "overdue");
  return `/manage/contracts/${contractId}?${params.toString()}`;
}

function variationsUrl(contractId: string): string {
  const params = new URLSearchParams();
  params.set("tab", "variations-sows");
  // PLURAL var_filters per the cross-tab nav contract — verified at
  // variationSowUrlState.ts.
  params.set("var_filters[status]", "proposed");
  return `/manage/contracts/${contractId}?${params.toString()}`;
}

function deliverableSubtitle(
  count: number,
  example: string | undefined,
  hasRecords: boolean,
): string {
  if (!hasRecords) return "no deliverables tracked yet";
  if (count === 0) return "nothing overdue";
  return example ?? "";
}

function milestoneSubtitle(
  count: number,
  example: string | undefined,
  hasRecords: boolean,
): string {
  if (!hasRecords) return "no milestones tracked yet";
  if (count === 0) return "all current milestones on track";
  return example ?? "";
}

function insuranceSubtitle(
  count: number,
  example: string | undefined,
  hasRecords: boolean,
): string {
  if (!hasRecords) return "no insurance tracked yet";
  if (count === 0) return "all coverage in force";
  return example ?? "";
}

function variationSubtitle(
  count: number,
  example: string | undefined,
  hasRecords: boolean,
): string {
  if (!hasRecords) return "no variations tracked yet";
  if (count === 0) return "no pending decisions";
  return example ?? "";
}

export function ContractHealthSummary({
  contractId,
  health,
  hasRecords,
}: ContractHealthSummaryProps) {
  const flags: HasRecordsFlags = {
    deliverables: hasRecords?.deliverables ?? true,
    milestones: hasRecords?.milestones ?? true,
    insurance: hasRecords?.insurance ?? true,
    variations: hasRecords?.variations ?? true,
  };

  const overdueDelCount = health.overdueDeliverables.count;
  const overdueMsCount = health.overdueMilestones.count;
  const insExpCount = health.insuranceExpiring.count;
  const pendVarCount = health.pendingVariations.count;

  const renderValue = (count: number, hasRecords: boolean) =>
    hasRecords ? String(count) : "—";

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
      data-testid="contract-health-summary"
    >
      <CrossTabSummaryCard
        testId="overdue-deliverables-card"
        label="Overdue deliverables"
        value={renderValue(overdueDelCount, flags.deliverables)}
        valueTone={overdueDelCount > 0 ? "danger" : "neutral"}
        subtitle={deliverableSubtitle(
          overdueDelCount,
          health.overdueDeliverables.nearestExample?.subtitle,
          flags.deliverables,
        )}
        ariaLabel={`View ${overdueDelCount} overdue deliverable${
          overdueDelCount === 1 ? "" : "s"
        }`}
        to={deliverablesUrl(contractId)}
      />
      <CrossTabSummaryCard
        testId="overdue-milestones-card"
        label="Overdue milestones"
        value={renderValue(overdueMsCount, flags.milestones)}
        valueTone={overdueMsCount > 0 ? "danger" : "neutral"}
        subtitle={milestoneSubtitle(
          overdueMsCount,
          health.overdueMilestones.nearestExample?.subtitle,
          flags.milestones,
        )}
        ariaLabel={`View ${overdueMsCount} overdue milestone${
          overdueMsCount === 1 ? "" : "s"
        }`}
        to={milestonesUrl(contractId)}
      />
      <CrossTabSummaryCard
        testId="insurance-expiring-card"
        label="Insurance expiring"
        value={renderValue(insExpCount, flags.insurance)}
        valueTone={insExpCount > 0 ? "warning" : "neutral"}
        subtitle={insuranceSubtitle(
          insExpCount,
          health.insuranceExpiring.nearestExample?.subtitle,
          flags.insurance,
        )}
        ariaLabel={`View ${insExpCount} expiring insurance record${
          insExpCount === 1 ? "" : "s"
        }`}
        to={buildInsuranceUrl(contractId, { status: "expiring" })}
      />
      <CrossTabSummaryCard
        testId="pending-variations-card"
        label="Pending variations"
        value={renderValue(pendVarCount, flags.variations)}
        valueTone={pendVarCount > 0 ? "warning" : "neutral"}
        subtitle={variationSubtitle(
          pendVarCount,
          health.pendingVariations.nearestExample?.subtitle,
          flags.variations,
        )}
        ariaLabel={`View ${pendVarCount} pending variation${
          pendVarCount === 1 ? "" : "s"
        }`}
        to={variationsUrl(contractId)}
      />
    </div>
  );
}

export default ContractHealthSummary;
